import { supabase } from '@/lib/supabase';

const ORG_ID = '00000000-0000-0000-0000-000000000001';

// Modèle simplifié de construction : une « ligne » = une question + un tarif.
// On la traduit en (question + règle) compatibles avec le moteur QCM existant.
export interface BuilderLigne {
  kind: 'quantite' | 'ouinon' | 'texte';
  question: string; // libellé de la question posée
  unite: string; // pour les quantités (m², ml, u…)
  prix: string; // prix unitaire HT
  tvaCode: string; // '20' | '10' | '5.5' | '0'
  libelle: string; // libellé de la ligne de devis (défaut = question)
}

export interface BuilderModele {
  nom: string;
  description: string;
  couleur: string;
  lignes: BuilderLigne[];
}

function slug(s: string): string {
  const base = s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return base === '' ? 'devis_guide' : base;
}

interface Clause {
  questionCode: string;
  op: 'eq' | 'gt';
  value: unknown;
}

function ligneToConditions(
  code: string,
  kind: BuilderLigne['kind'],
): { operator: 'AND'; clauses: Clause[] } | null {
  if (kind === 'quantite') {
    return { operator: 'AND', clauses: [{ questionCode: code, op: 'gt', value: 0 }] };
  }
  if (kind === 'ouinon') {
    return { operator: 'AND', clauses: [{ questionCode: code, op: 'eq', value: true }] };
  }
  return null; // texte : pas de condition ; le moteur saute la ligne si la réponse est vide
}

// Écrit (section + questions + règles) pour un modèle donné.
async function ecrireLignes(modeleId: string, lignes: BuilderLigne[]): Promise<void> {
  const { data: sec, error: secErr } = await supabase
    .from('questionnaire_sections')
    .insert({ organization_id: ORG_ID, modele_id: modeleId, titre: 'Questions', ordre: 0 })
    .select('id')
    .single();
  if (secErr) throw new Error(secErr.message);
  const sectionId = (sec as { id: string }).id;

  const valides = lignes.filter((l) => l.question.trim() !== '');

  const questionsRows = valides.map((l, i) => ({
    organization_id: ORG_ID,
    section_id: sectionId,
    code: `q${i + 1}`,
    libelle: l.question.trim(),
    type: l.kind === 'quantite' ? 'nombre' : l.kind === 'ouinon' ? 'booleen' : 'texte',
    unite: l.kind === 'quantite' && l.unite.trim() !== '' ? l.unite.trim() : null,
    obligatoire: false,
    ordre: i,
  }));
  if (questionsRows.length > 0) {
    const { error } = await supabase.from('questions').insert(questionsRows);
    if (error) throw new Error(error.message);
  }

  const reglesRows = valides.map((l, i) => {
    const code = `q${i + 1}`;
    const libelle = l.libelle.trim() === '' ? l.question.trim() : l.libelle.trim();
    const prix = String(Number(l.prix.replace(',', '.')) || 0);
    return {
      organization_id: ORG_ID,
      modele_id: modeleId,
      nom: l.kind === 'texte' ? l.question.trim() : libelle,
      conditions: ligneToConditions(code, l.kind),
      action: 'add_quote_line',
      action_params:
        l.kind === 'texte'
          ? {
              libelleFromCode: code,
              quantiteFormule: '1',
              prixUnitaireFormule: prix,
              tauxTvaCode: l.tvaCode,
            }
          : {
              libelleOverride: libelle,
              quantiteFormule: l.kind === 'quantite' ? code : '1',
              prixUnitaireFormule: prix,
              tauxTvaCode: l.tvaCode,
            },
      ordre: i,
      actif: true,
    };
  });
  if (reglesRows.length > 0) {
    const { error } = await supabase.from('question_regles').insert(reglesRows);
    if (error) throw new Error(error.message);
  }
}

export async function creerQuestionnaire(input: BuilderModele): Promise<string> {
  const code = `${slug(input.nom)}_${crypto.randomUUID().slice(0, 6)}`;
  const { data: mod, error } = await supabase
    .from('questionnaire_modeles')
    .insert({
      organization_id: ORG_ID,
      code,
      nom: input.nom.trim(),
      description: input.description.trim() === '' ? null : input.description.trim(),
      couleur: input.couleur,
      actif: true,
      version: 1,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  const modeleId = (mod as { id: string }).id;
  await ecrireLignes(modeleId, input.lignes);
  return modeleId;
}

interface BuilderRow {
  nom: string;
  description: string | null;
  couleur: string | null;
  questionnaire_sections: {
    questions: { code: string; libelle: string; type: string; unite: string | null }[];
  }[];
}
interface RegleRow {
  conditions: { clauses?: { questionCode?: string }[] } | null;
  action_params: {
    libelleOverride?: string;
    libelleFromCode?: string;
    prixUnitaireFormule?: string;
    tauxTvaCode?: string;
  } | null;
}

export async function getQuestionnaireBuilder(id: string): Promise<BuilderModele> {
  const [modRes, regRes] = await Promise.all([
    supabase
      .from('questionnaire_modeles')
      .select('nom,description,couleur,questionnaire_sections(questions(code,libelle,type,unite))')
      .eq('id', id)
      .single(),
    supabase
      .from('question_regles')
      .select('conditions,action_params,ordre')
      .eq('modele_id', id)
      .eq('actif', true)
      .order('ordre'),
  ]);
  if (modRes.error) throw new Error(modRes.error.message);
  if (regRes.error) throw new Error(regRes.error.message);

  const m = modRes.data as unknown as BuilderRow;
  const qByCode = new Map<string, { libelle: string; type: string; unite: string | null }>();
  for (const s of m.questionnaire_sections ?? []) {
    for (const q of s.questions ?? []) qByCode.set(q.code, q);
  }
  const regles = (regRes.data ?? []) as unknown as RegleRow[];
  const lignes: BuilderLigne[] = regles.map((r) => {
    // Pour une question texte, il n'y a pas de condition : le code vient de libelleFromCode.
    const code =
      r.conditions?.clauses?.[0]?.questionCode ?? r.action_params?.libelleFromCode ?? '';
    const q = qByCode.get(code);
    const t = q?.type;
    return {
      kind: t === 'booleen' ? 'ouinon' : t === 'texte' || t === 'texte_long' ? 'texte' : 'quantite',
      question: q?.libelle ?? '',
      unite: q?.unite ?? '',
      prix: String(r.action_params?.prixUnitaireFormule ?? ''),
      tvaCode: r.action_params?.tauxTvaCode ?? '20',
      libelle: r.action_params?.libelleOverride ?? '',
    };
  });
  return {
    nom: m.nom,
    description: m.description ?? '',
    couleur: m.couleur ?? '#1D4595',
    lignes,
  };
}

export async function majQuestionnaire(id: string, input: BuilderModele): Promise<void> {
  const { error: upErr } = await supabase
    .from('questionnaire_modeles')
    .update({
      nom: input.nom.trim(),
      description: input.description.trim() === '' ? null : input.description.trim(),
      couleur: input.couleur,
    })
    .eq('id', id);
  if (upErr) throw new Error(upErr.message);

  const { error: dr } = await supabase.from('question_regles').delete().eq('modele_id', id);
  if (dr) throw new Error(dr.message);
  const { error: ds } = await supabase.from('questionnaire_sections').delete().eq('modele_id', id);
  if (ds) {
    throw new Error(
      'Ce devis guidé a déjà servi à générer des devis : impossible de modifier ses questions. Créez-en un nouveau.',
    );
  }
  await ecrireLignes(id, input.lignes);
}

export async function desactiverQuestionnaire(id: string): Promise<void> {
  const { error } = await supabase
    .from('questionnaire_modeles')
    .update({ actif: false })
    .eq('id', id);
  if (error) throw new Error(error.message);
}
